// src/components/desk/DealCloseModal.jsx — the ONE confirm for closing a deal
// ----------------------------------------------------------------------------
// Owner ruling 2026-07-30: closing needs its own fields — FINAL price and FINAL
// commission. They inherit from the agent's cooperation participation, but terms
// drift during a deal, so the agent confirms or corrects them, THEN closes.
// One PANEL, two doors: the meeting's "დასრულებული" status pill and the სტატუსი
// card — same fields, same confirm, same records.
//
// ⚠️ ONE implementation, NO shell (owner 2026-07-30): both doors already sit
// inside a modal, and stacking a second overlay over them read as "a modal opened
// in the background". So `DealClosePanel` carries no shell — each door expands it
// in place. Do not fork the fields or the confirm logic per door.
//
// ════════════════════════════════════════════════════════════════════════════
// ⚠️⚠️ THE CLOSE GOES THROUGH record_deal_closure. NOTHING ELSE MAY END A DEAL.
// ════════════════════════════════════════════════════════════════════════════
// Owner report 2026-07-30: *"I was able to record close deal twice from two places,
// this is a bug and closed deal must be synched."* Verified in the DB — property
// c4a0ce00 carried TWO ended/deal_closed cooperations 48 seconds apart, one `rent`
// and one `sale`, with different final numbers.
//
// The cause was not the two doors. It was that this panel hand-rolled the close
// (amend_cooperation + set_cooperation_status) instead of calling the writer that
// already owns it. `end_reason_policy` literally names that writer as the authority
// for reason `deal_closed`: "meetings flow (record_deal_closure, 0086)".
//
// What routing through it fixes, none of which a second door-level guard could:
//   • It ends EVERY matching cooperation on the property in one statement, so a
//     second door finds nothing left to close. The duplicate was structurally
//     impossible for DealsTab, which has always used this path.
//   • It selects those cooperations by OUTCOME (sold → sale; rented_out → rent +
//     girao), so the deal TYPE is never picked by hand. The sale/rent split in the
//     duplicate came from this panel asking the agent to choose an intent — and
//     defaulting to 'sale' on the meeting door, which passes none. That whole
//     question is now gone: the server derives the outcome from listing.intent
//     (B118) when the caller does not send one.
//   • It records the lifecycle observation on the LISTING (status_sold /
//     status_rented_out → reduce_listing_state → listing.status via 0180). THAT is
//     the sync the owner asked for: feed, search, leads and the match gate all read
//     listing state, and a hand-rolled cooperation end left every one of them
//     believing the property was still on the market.
//   • It emits `deal.closed` per closing agent — the ranking's points source. Every
//     close recorded through the old hand-rolled path earned the agent NO credit.
//
// This panel therefore owns exactly two things the writer does not:
//   1. the FINAL terms on the agent's own participation (amend_cooperation — final
//      values are recorded facts; min_price stays what the owner ASKED), and
//   2. closing the meeting it was opened from.
// Commission also travels into the closure's own `meta`, so it is recorded even when
// there is no cooperation row to hang it on.
//
// ⚠️ THE AD IS CHOSEN, NEVER INFERRED (review ruling 2026-07-30). An earlier version of this
// panel fell back to `cooperation_listing` when the door had no listing. That is a silent
// corruption vector: one property carries expired ads, several agencies' postings, a rent AND a
// sale ad, reposts and duplicate marketplace entries — so inferring means an algorithm decides
// which ad counts as sold and which agent earns the attribution. The server is fail-closed
// (POST /api/core/property/:id/deal-closed 422s with `candidates` when listing_id is absent)
// and this panel renders the picker. A human is slower than a heuristic and much cheaper than
// corrupted attribution.
//
// ⚠️ DELETED 2026-07-30, do not bring back: an earlier fix here (mine) responded to
// "no live cooperation → the fields are disabled" by OPENING a cooperation against
// the resolved owner contact and immediately ending it. That invented a relationship
// to have somewhere to write a number, and it is what minted the duplicate above.
// The number's honest home was already `commission_amount` in the closure meta.

import { useEffect, useState } from 'react'
import { DealSplit } from './DealSplit.jsx'
import { t, tLog, tOr } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { input, btn } from './primitives.jsx'
import { amendCooperation, correctDealFigures, getCooperation, getCoreViewerAgentId, getDealCloseCandidates, listCooperations, openCooperation, reportPropertyDealClosure, updateMeeting } from '../../db/core.js'
import { commissionAmount as sharedCommissionAmount } from '../../lib/coopMath.js'
import { ourShareOf, splitAmounts } from '../../../core/cobroke-rules.mjs'
import { parseMoneyInput, sanitizeMoneyInput } from '../../lib/moneyInput.js'

const positiveFigure = v => {
  const n = parseMoneyInput(v)
  return n != null && n > 0 ? n : null
}

// Shell-free by design: BOTH doors are already inside a modal, so this expands in
// place. If a future door needs its own overlay, wrap this in <Modal> at the call
// site — do not re-add a wrapper here and do not fork the fields.
//
// listingId: the ad the closure is recorded against. record_deal_closure is keyed on a
// LISTING (it writes that listing's lifecycle), so a door that cannot supply one cannot
// record a closure — it says so rather than closing something adjacent.
// lifecycle: the PROPERTY-level closed state, derived by the caller across the listing
// family (owner ruling 2026-07-30: "derive it in the UI, don't mark siblings"). It is the
// honest gate for this door — the cooperation-based check below misses every close recorded
// without a cooperation (which this panel itself allows), and that miss is exactly how the
// owner closed the same deal twice. The server 409s regardless; this prop is what lets the
// door SAY closed instead of letting the agent fill a form the server will reject.
// commissionSplit (0449): the co-broke ratio recorded on the meeting this door was opened
// from, [{side,party_id,percent,name?}]. Passed IN rather than fetched here — the meeting
// door already has it, and a second fetch is a second chance to disagree with the panel the
// agent was just reading.
//
// ⚠️ What it changes is WHICH NUMBER REACHES PAYROLL. The commission field below is the whole
// fee; `commission_amount` in the closure is OUR half. Without this the agency's payroll
// (agentDealIncome) would divide the other agency's money among our agents, and the
// leaderboard would rank on it.
export function DealClosePanel({ propertyId, listingId = null, meetingId = null, lifecycle = null,
                                 commissionSplit = null, cobrokeExpected = null, deal = null, onClose, onClosed,
                                 role = 'agent', agentsById = {}, splitPreview = null,
                                 // undefined = the door does not know (property card); null = the meeting HAS no client.
                                 clientPartyId = undefined }) {
  const [notice, setNotice] = useState(null)
  const [coop, setCoop] = useState(undefined)   // undefined = loading, null = none live
  const [mine, setMine] = useState(null)
  const [price, setPrice] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [commission, setCommission] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  // ALREADY CLOSED — the other door got there first. Kept even though routing through
  // record_deal_closure makes a duplicate structurally impossible: the agent should see
  // WHAT was recorded, not just be prevented from recording again.
  // What the close FROZE, handed back by the server so the agent can see what they earned
  // without a second round trip (owner ask 2026-08-29). Inputs only — the rates, who attended
  // and the commission; the division itself is computed by DealSplit with the one shared rule.
  const [split, setSplit] = useState(null)
  const [closedAlready, setClosedAlready] = useState(null)
  // ── CORRECTING the figures of a deal already closed (owner 2026-08-26) ────────────────
  // "If we have made it required while closing we don't need later add but we may add edit."
  // So this is deliberately NOT an empty form: it opens holding what is recorded, and the
  // save refuses to empty either field, exactly as the close does. `editVals === null` means
  // the door is shut, which is its resting state — a closed deal is a fact, and correcting
  // one is a deliberate act, not a set of inputs sitting open waiting to be nudged.
  const [editVals, setEditVals] = useState(null)   // { price, commission, currency, reason }
  const [editBusy, setEditBusy] = useState(false)
  const [editErr, setEditErr] = useState('')
  const recorded = deal ? {
    price: deal.closing_terms?.final_price ?? deal.final_price ?? null,
    commission: deal.closing_terms?.final_commission_amount ?? deal.final_commission_amount ?? null,
    currency: deal.closing_terms?.final_currency || deal.final_currency || 'USD',
  } : null
  async function saveFigures() {
    if (editBusy || !deal?.id || !editVals) return
    setEditBusy(true); setEditErr('')
    try {
      await correctDealFigures(deal.id, {
        final_price: editVals.price === '' ? null : parseMoneyInput(editVals.price),
        final_commission_amount: editVals.commission === '' ? null : parseMoneyInput(editVals.commission),
        final_currency: editVals.currency,
        reason: editVals.reason || null,
      })
      setEditVals(null)
      onClosed?.()          // the parent refetches; the card redraws with the corrected line
    } catch (e) {
      setEditErr(e.message === 'figures_required' ? t('deal_close_figures_required')
        : e.message === 'split_unreadable' ? t('deal_close_split_unreadable') : e.message)
    } finally { setEditBusy(false) }
  }
  // Candidate ads are loaded for every door so an already-closed sibling is detected before
  // a write. The picker is shown only when the door brought no listing. undefined = loading;
  // [] = this property has no ad at all.
  const [candidates, setCandidates] = useState(undefined)
  const [candidateErr, setCandidateErr] = useState(false)
  const [candidateVer, setCandidateVer] = useState(0)
  const [pickedListing, setPickedListing] = useState(null)
  // 0449. Normalised once: every reader below treats "no co-broke" as an empty list, so
  // there is no null-vs-[] question anywhere in the render or the write.
  const cobrokeSplit = Array.isArray(commissionSplit) ? commissionSplit : []
  // ⚠️ AN OUTSIDER WITH NO RECORDED RATIO IS NOT "WE KEEP EVERYTHING" (owner 2026-09-02:
  // "at final calculation step not seeing it"). With no split rows this panel divided the
  // WHOLE fee internally — $250 across our agents and the company — on a deal whose property
  // representative is another agency's. That is the same money defect the meeting modal
  // fixed one screen earlier, arriving through the door that actually writes to payroll.
  // It REFUSES rather than guesses: inventing the standard 50% here would record a ratio
  // nobody agreed, and closing at the gross would pay our agents out of her half.
  const cobrokeMissing = !!cobrokeExpected && !cobrokeSplit.length
  // OUR half of what the agent typed. null when the fee is still blank — never 0, so the
  // panel can stay silent rather than announce that we earn nothing.
  const ourNet = cobrokeSplit.length ? ourShareOf(parseMoneyInput(commission), cobrokeSplit) : null
  // Preview the exact client-side payroll rule before the irreversible confirmation. The
  // meeting supplies WHO is involved + today's rates; after close the server snapshot replaces
  // these inputs with the frozen record and the same DealSplit component renders it again.
  const previewCommission = cobrokeSplit.length ? ourNet : positiveFigure(commission)
  const prospectiveSplit = splitPreview?.agent_links?.length && previewCommission != null
    ? { ...splitPreview, commission_amount: previewCommission, commission_currency: currency }
    : null

  useEffect(() => {
    let gone = false
    ;(async () => {
      try {
        if (!propertyId) { if (!gone) setCoop(null); return }
        const rows = await listCooperations({ propertyId })
        // Read the ENDED rows too — that is how this door learns the other one fired.
        const closed = (rows || []).find(c => c.status === 'ended' && c.ended_reason === 'deal_closed') || null
        const live = (rows || []).find(c => !['ended', 'rejected'].includes(c.status)) || null
        if (gone) return
        if (closed && !live) { setCoop(null); setClosedAlready(closed); return }
        if (!live) { setCoop(null); return }
        const d = await getCooperation(live.id)
        if (gone) return
        setCoop(d)
        // Seed from the viewer's OWN participation (their negotiation), falling back
        // to the rolled-up parent — a SUGGESTION layer only, every field editable.
        const own = (d.participants || []).filter(p => !p.left_at).find(p => p.is_self) || null
        setMine(own)
        // ⚠️ SEED ONLY FROM THE VIEWER'S OWN PARTICIPATION (fixed 2026-08-15). This used to
        // fall back to the cooperation PARENT, whose term columns are the roll-up of whoever
        // confirmed last — and `mine` is null exactly when the cooperation belongs to a
        // COLLEAGUE, which this file's own comment calls "the ordinary case for a viewing on
        // a colleague's property". So the commonest close pre-filled a colleague's figures,
        // under a hint reading "inherited from YOUR cooperation terms", and the join-on-close
        // path then wrote them back as this agent's final terms while the same number went to
        // `commission_amount` → agentDealIncome → the leaderboard. Two other surfaces already
        // forbid exactly this (MeetingModal's terms row, CooperationModal's load guard); the
        // one that WRITES was the one still doing it.
        // With no own participation the fields stay empty: the agent types what was agreed,
        // which is the only honest source for a final number nobody has recorded.
        setCurrency(own?.min_price_currency || 'USD')
        if (own) {
          if (own.min_price != null) setPrice(String(own.min_price))
          const amt = sharedCommissionAmount({
            type: own.terms?.commission_type || 'percent', price: own.min_price,
            percent: own.commission_percent, ownerNet: own.terms?.owner_net_amount,
            net: own.terms?.net_commission_amount,
          })
          // ⚠️ Keep the cents. `Math.round` here discarded up to half a currency unit on
          // every closed deal BEFORE the number became the written final commission —
          // commissionAmount returns cents by design (coopMath rounds to 2dp), and this was
          // the one place that threw them away. Trailing ".00" is trimmed so the ordinary
          // whole-number case still reads as a whole number.
          if (amt != null && Number.isFinite(amt)) setCommission(String(Math.round(amt * 100) / 100))
        }
      } catch { if (!gone) setCoop(null) }
    })()
    return () => { gone = true }
  }, [propertyId])

  // The picker's options. Fetched up front rather than only on the 422, so the agent chooses
  // before committing instead of being corrected afterwards — the server still 422s with the
  // same list, which is what makes the rule hold for any caller.
  // Fetched for EVERY door, not just the listing-less ones: candidates are property-scoped
  // and carry per-ad lifecycle, which makes them the one read that always sees a recorded
  // close. The lineage family a door passes lifecycle from can be a FRAGMENT (verified live
  // 2026-07-30: the closed ad of property faa6623c is absent from a sibling ad's family), so
  // without this a door on the fragment would offer the form and only learn "closed" from
  // the server's 409. The list renders as a picker only when the door brought no listing.
  useEffect(() => {
    setPickedListing(null)
    setCandidateErr(false)
    if (!propertyId) { setCandidates(undefined); return }
    setCandidates(undefined)
    let gone = false
    getDealCloseCandidates(propertyId)
      .then(d => { if (!gone) setCandidates(d?.candidates || []) })
      // A failed read is not evidence that the property has no adverts. The old [] fallback
      // confidently showed “no listing”, sending the agent away from a perfectly valid deal.
      .catch(() => { if (!gone) { setCandidates(undefined); setCandidateErr(true) } })
    return () => { gone = true }
  }, [propertyId, listingId, candidateVer])

  // WHICH AD the closure is recorded against. The door's own listing when it has one, else
  // whatever the agent PICKED from the candidate list. Never inferred — see the header.
  const effectiveListingId = listingId || pickedListing || null
  // Closed is closed, whichever record says so: the family-derived lifecycle (covers closes
  // recorded with no cooperation) OR an ended-deal_closed cooperation (carries the final
  // terms worth showing). Either one shuts this door.
  // Doors that know nothing about the property (the meeting modal passes no lifecycle) still
  // learn "closed" from the candidate list itself — each candidate carries `class_closed`,
  // meaning "this ad's KIND of deal (sale vs tenancy) is already recorded closed on this
  // property".
  //
  // ⚠️ Scoped per class, never property-wide (audit fix 2026-07-30). A flat that was rented out
  // may still be for sale; treating any terminal ad as "the property is done" would have shut
  // this door on live sale work. Closed here means: every ad we could still close is spoken for.
  const TERMINAL = ['status_sold', 'status_rented_out']
  const openCandidates = Array.isArray(candidates) ? candidates.filter(c => !c.class_closed) : null

  // ── Grouping for the picker (2026-08-01) ───────────────────────────────────
  // THREE buckets in one pass, in the order an agent scans: their own ads, then other
  // agents' ads, then anything no longer closable (already-recorded deals and ads whose
  // class is spoken for — kept visible, never dropped, per the note on the rows below).
  // Within a bucket, active ads first, then newest-seen — a live ad is the likely answer
  // and an expired one is the exception.
  //
  // ⚠️ PRESENTATION ONLY. No preselection, no filtering, no inference of which ad closed:
  // ordering a list is not choosing from it. `is_mine` comes from the server (the ad's
  // author party vs the viewer's) and is a LABEL — if it is ever wrong the agent still
  // sees every ad and still picks.
  // The ad the door brought, resolved against the candidate list so it can be NAMED rather
  // than just used. Null until candidates load, or for a listing outside this property's
  // family — the line simply does not render then; it is a label, never a gate.
  const inheritedAd = listingId && Array.isArray(candidates)
    ? candidates.find(c => c.listing_id === listingId) || null
    : null
  const groupedCandidates = (() => {
    const rows = Array.isArray(candidates) ? candidates : []
    if (!rows.length) return []
    const rank = c => (c.status === 'active' ? 0 : 1)
    const sorted = [...rows].sort((a, b) =>
      rank(a) - rank(b) || String(b.last_seen_at || '').localeCompare(String(a.last_seen_at || '')))
    const buckets = [
      [t('deal_close_group_mine'), sorted.filter(c => c.is_mine && !c.class_closed)],
      [t('deal_close_group_others'), sorted.filter(c => !c.is_mine && !c.class_closed)],
      [t('deal_close_group_done'), sorted.filter(c => c.class_closed)],
    ]
    return buckets.filter(([, rs]) => rs.length)
  })()
  const chosenClosed = !!(listingId && Array.isArray(candidates)
    && candidates.find(c => c.listing_id === listingId)?.class_closed)
  const nothingLeft = Array.isArray(candidates) && candidates.length > 0 && openCandidates.length === 0
  const isClosed = TERMINAL.includes(lifecycle) || chosenClosed
  // THE PERCENT-AS-AMOUNT TRAP (owner-reported 2026-09-29, Kristi's 26 Sep deal): the closer had no
  // terms of their own, so the boxes were empty — correctly, see the hint below — and the only
  // number on screen was the cooperation's "1,000 USD / 50%". "50" went into the AMOUNT box and a
  // 500 USD fee was recorded as 50. So: name the visible cooperation terms beside the box as a
  // worked sum, and stop a close whose amount equals that percentage. Still never a prefill.
  const coopTerms = (() => {
    const rows = (coop?.participants || []).filter(p => !p.left_at && p.commission_percent != null && p.min_price != null)
    const r = rows[0]
    if (!r) return null
    const pct = Number(r.commission_percent), base = Number(r.min_price)
    if (!Number.isFinite(pct) || !Number.isFinite(base) || pct <= 0 || base <= 0) return null
    return { pct, base, amount: Math.round(base * pct) / 100, currency: r.min_price_currency || 'USD' }
  })()
  const [percentOk, setPercentOk] = useState(false)   // the closer confirmed "yes, the amount really is that"
  useEffect(() => { setPercentOk(false) }, [commission])
    || (!listingId && nothingLeft) || !!closedAlready
  // The fields are editable whenever a closure can actually be recorded. No cooperation
  // is fine — the numbers ride the closure's meta.
  // ── FIGURES ARE REQUIRED (owner ruling 2026-08-26) ──────────────────────────────────
  // "We need to make terms fields required to close a deal." A deal recorded with no price
  // and no fee cannot answer the one question asked of it afterwards — and until 0728 the
  // price reached only the cooperation, so a close without one recorded it nowhere. The
  // server refuses this route without both (400 figures_required); the button says so first.
  const hasFigures = positiveFigure(price) != null && positiveFigure(commission) != null
  const canClose = !!effectiveListingId && !isClosed && Array.isArray(candidates) && !candidateErr
  const editable = canClose
  // Money fields hold DIGITS (plus one decimal point). Free text here let the owner type
  // "ccscd", which parseFloat turned into NaN and JSON silently shipped as null — a wrong
  // value recorded as "no value". Blocking the characters beats validating them later.
  // A comma is the decimal separator this office types ("550,50"); stripping it silently
  // turned that into 55050 (audit 2026-09-07). Money is never negative here, so a minus is
  // noise like any other letter.
  // ⚠️ Review 2026-09-23: converting the comma HERE turned "120,000" into 120 — the box keeps
  // the separators as typed and lib/moneyInput.js reads them once, by position.
  const numeric = sanitizeMoneyInput

  async function confirm() {
    if (busy) return
    setBusy(true); setErr(null)
    try {
      // Belt to the input filter's suspenders: anything non-finite is refused loudly, never
      // shipped as null. A blank field IS null — "didn't state one", not zero.
      const num = v => {
        if (v === '' || v == null) return null
        const n = parseMoneyInput(v)
        if (n == null) throw new Error(t('deal_close_bad_number'))
        return n
      }
      // ⚠️ A SPLIT THAT WILL NOT SAY WHAT WE KEEP CANNOT CLOSE A DEAL (2026-08-15). `ourNet`
      // is null in exactly that case, and `commission_amount` is what pays and ranks our
      // agents — writing null there records "no commission" on a deal that has one, and the
      // old behaviour (the gross) was worse. Refused here, loudly, rather than silently
      // stored: the ratio is two taps away in the panel above.
      // The percent-as-amount guard: the amount typed IS the cooperation's percentage and differs
      // from the sum it implies. Stop once and say both numbers; a second press goes through.
      if (coopTerms && !percentOk && num(commission) === coopTerms.pct && coopTerms.amount !== coopTerms.pct) {
        setPercentOk(true)
        throw new Error(tLog('deal_close_percent_guard', { pct: coopTerms.pct, base: coopTerms.base.toLocaleString(),
          amount: coopTerms.amount.toLocaleString(), cur: coopTerms.currency }))
      }
      if (cobrokeSplit.length && num(commission) !== null && ourNet === null) {
        throw new Error(t('deal_close_split_unreadable'))
      }
      // 1. FINAL terms on the agent's own participation, when there is a cooperation to
      //    carry them. Done BEFORE the close: record_deal_closure ends the row, and
      //    amend_cooperation on an ended cooperation is not a thing we want to rely on.
      if (coop) {
        const ops = []
        if (num(price) != null) {
          ops.push({ op: 'set', field: 'terms.final_price', value: num(price) })
          ops.push({ op: 'set', field: 'terms.final_currency', value: currency })
        }
        // ⚠️ THE GROSS, deliberately, even on a co-broked deal (0449). This is the
        // cooperation's own record — what the OWNER agreed to pay for this property — and
        // it must not become our net. What we kept out of it is the closure's business
        // (commission_amount below), and confusing the two would make the next agent read
        // a lower fee than the owner actually offered.
        if (num(commission) != null) ops.push({ op: 'set', field: 'terms.final_commission_amount', value: num(commission) })
        if (ops.length) {
          // ── JOIN-ON-CLOSE (owner-caught 2026-08-01) ───────────────────────────
          // These ops are PARTICIPANT-OWNED (final price / final commission are one
          // agent's negotiated facts, not the shared parent's), so the server demands a
          // participant to hang them on. `mine` is null whenever the cooperation belongs
          // to ANOTHER agent — the ordinary case for a viewing on a colleague's property
          // — and the close then died on "participant_party_id required for
          // participant-owned terms" with the deal already agreed. The agent read it as
          // "I can't close my own meeting".
          //
          // Recording your final commission on a deal you closed IS participation, which
          // is the same ruling that governs the cooperation form (2026-07-29: "if an
          // agent writes in and saves, that already means they're in"). So join first,
          // then write to the row that join created.
          //
          // ⚠️ This is a JOIN, never an invention — it is inside `if (coop)` and passes
          // THAT cooperation's own contact and intent, so find-or-create lands on the
          // existing row. Do not lift it outside this branch: opening a cooperation
          // because there was nowhere to put a number is the exact 2026-07-30 bug in the
          // header above, and it minted a duplicate deal.
          let scope = mine?.party_id || null
          if (!scope && coop.contact_party_id) {
            try {
              await openCooperation({
                propertyId, contactPartyId: coop.contact_party_id,
                transactionIntent: coop.transaction_intent,
              })
              const fresh = await getCooperation(coop.id)
              scope = (fresh.participants || []).filter(p => !p.left_at).find(p => p.is_self)?.party_id || null
            } catch (e) { console.warn('[deal-close] join before terms failed:', e.message) }
          }
          // Still no row to write to → close ANYWAY without the cooperation amend. The
          // commission travels in the closure's own meta regardless (see step 2), so the
          // money is recorded either way — and refusing to close an agreed deal over a
          // bookkeeping row is the worse failure.
          if (scope) await amendCooperation(coop.id, ops, scope)
          else console.warn('[deal-close] no participation to carry final terms — closing with commission in the closure meta only')
        }
      }
      // 2. THE close — one writer, same one DealsTab's viewing close uses. `outcome` is
      //    deliberately OMITTED: the server derives it from listing.intent (B118), which
      //    knows the answer instead of guessing it. Commission travels in meta so it is
      //    recorded even with no cooperation row.
      // ── WHO CLOSED IT: THE AGENTS ON THE DEAL, not whoever pressed the button ──────────
      // Owner report 2026-09-02, signed in as ADMIN: „აგენტის ვინაობა ვერ განისაზღვრა —
      // გარიგების მიწერა შეუძლებელია", with the money above it computed correctly. An admin
      // has no `agents` row by design (CLAUDE.md says so in four places), so
      // getCoreViewerAgentId() is null and this refused before it ever called the server —
      // management could not close ANY deal.
      //
      // The fix is not to wave the check through: `scheduler_agent_ids` is what earns each
      // agent their `deal.closed` event, i.e. ranking points and attribution. Sending the
      // VIEWER was wrong even when the viewer had an id — an agent closing a colleague's
      // deal would have taken the credit for it. The honest answer is the set the meeting
      // already resolved: the involved agents whose links this panel is about to preview.
      //
      // The viewer stays as the FALLBACK for the property-card door, which carries no
      // meeting and therefore no involved set — unchanged behaviour there.
      const involvedIds = [...new Set((splitPreview?.agent_links || [])
        .map(l => l.scheduler_agent_id).filter(Boolean))]
      const viewerAgentId = getCoreViewerAgentId()
      const closingAgentIds = involvedIds.length ? involvedIds : (viewerAgentId ? [viewerAgentId] : [])
      if (!closingAgentIds.length) throw new Error(t('deal_close_no_viewer'))
      const closeResult = await reportPropertyDealClosure(propertyId, {
        listing_id: effectiveListingId,          // EXPLICIT, always. The server 422s without it.
        scheduler_agent_ids: closingAgentIds,
        viewing_id: meetingId || null,
        // ⚠️ OUR SHARE, not the fee (0449). `commission_amount` flows to
        // viewings.commission_amount and from there into agentDealIncome, which divides it
        // among our agents by their personal rates — so on a co-broked deal the gross would
        // pay our agents out of the other agency's half and rank them on it too. The gross
        // and the ratio ride alongside so the fact is not lost: "what did we give away" is
        // exactly the question asked when the co-broke standard is reviewed.
        // ⚠️ `ourNet` is now null when a split EXISTS but does not name our share (2026-08-15,
        // cobroke-rules). That is unknown, not zero and not the gross — so refuse the write
        // rather than record a figure nobody agreed. The guard is here because this is the
        // line the money leaves through.
        commission_amount: cobrokeSplit.length ? ourNet : num(commission),
        commission_currency: num(commission) != null ? currency : null,
        // 0728 — the deal keeps its own copy, so an ended cooperation is no longer the only
        // witness to what it closed at, and a close with no cooperation records one at all.
        price: num(price),
        ...(cobrokeSplit.length ? {
          commission_gross_amount: num(commission),
          commission_split: cobrokeSplit.map(r => ({ side: r.side, party_id: r.party_id, percent: r.percent })),
        } : {}),
      })
      // The split, if the server managed to freeze one. A missing snapshot means the stamp
      // failed (it is best-effort by design, so a close is never blocked by it) — showing
      // nothing is right; showing zeros would claim the deal paid nobody.
      if (closeResult?.snapshot) setSplit(closeResult.snapshot)
      // The row could not be minted (a meeting with no client — the column is NOT NULL). The
      // money IS recorded and counted (event-only closes, /agent-deals); what is missing is the
      // link to a client. Said in words, once, where the close was pressed (2026-09-06).
      if (closeResult?.deal_row === 'not_created') setNotice(t('deal_close_deal_row_missing'))
      // 3. The meeting this was opened from — a SEPARATE call that used to be able to
      // fail AFTER the closure landed, stranding a half-closed state: listing sold,
      // cooperation ended, commission credited, meeting still "scheduled" — and the
      // retry hit already_closed with no path left to flip it (fixed 2026-08-15).
      if (meetingId) {
        try { await updateMeeting(meetingId, { status: 'closed' }) }
        catch {
          try { await updateMeeting(meetingId, { status: 'closed' }) }   // one immediate retry
          catch {
            // The deal IS recorded. Say exactly that, refresh the parent so the closure
            // shows, and leave the panel up — pressing close again reaches the
            // already_closed recovery below, which finishes the flip.
            setErr(t('deal_close_status_flip_failed'))
            setBusy(false)
            onClosed?.()
            return
          }
        }
      }
      onClosed?.()
      // WITH a split to show, the panel STAYS OPEN. The owner asked for the agent to see what
      // they earned from this deal, and the old path dismissed the panel the instant the write
      // landed — there was no moment in which anything could be shown. The parent is refreshed
      // either way; only the dismissal waits. No snapshot (the stamp is best-effort) → behave
      // exactly as before and close.
      if (closeResult?.snapshot) { setBusy(false); return }
      onClose?.()
    } catch (e) {
      // already_closed with a meeting in hand is the RECOVERY DOOR for a half-close:
      // the closure exists (possibly written by this very panel a moment ago) and only
      // the meeting's flip is owed — finish it instead of dead-ending on a message.
      if (e.message === 'already_closed' && meetingId) {
        try {
          await updateMeeting(meetingId, { status: 'closed' })
          onClosed?.(); onClose?.(); return
        } catch { /* fall through to the sentence */ }
      }
      // The server's own guard fired — some other surface closed this property between our
      // read and this click. Say "already closed" in words, not an error code.
      setErr(e.message === 'already_closed' ? t('deal_close_already_closed') : e.message)
      setBusy(false)
    }
  }

  return (
      <div className="space-y-3">
        {/* JUST CLOSED — what the deal paid, before anything else on the panel. The agent who
            pressed the button is the one who wants this, so it comes first and the form is
            replaced rather than pushed down. DealSplit decides WHOSE lines are visible. */}
        {split ? (
          <div className="space-y-3">
            <p className="text-xs text-emerald-700 dark:text-emerald-300">
              <Icon name="check" /> {t('deal_close_recorded')}
            </p>
            <DealSplit snapshot={split} viewerAgentId={getCoreViewerAgentId()} role={role} agentsById={agentsById} />
            <button onClick={() => { setSplit(null); onClose?.() }}
              className="w-full px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
              {t('deal_split_done')}
            </button>
          </div>
        ) : coop === undefined ? (
          <p className="text-sm text-muted-foreground">{t('desk_loading')}</p>
        ) : isClosed ? (
          // The OTHER door already closed this deal. Show what was recorded rather than
          // offering to record it again — a closed deal is a fact about the property, and
          // both doors must agree about it.
          <div className="rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 space-y-1">
            <p className="text-xs text-emerald-700 dark:text-emerald-300">
              <Icon name="check" /> {t('deal_close_already_closed')}
            </p>
            {/* The recorded figures, and the door to correct them. Shown only with a deal to
                correct (a legacy closure has no row to PATCH) — a button that cannot act is
                worse than no button. */}
            {deal?.id && (recorded?.price != null || recorded?.commission != null) && (
              <div className="text-xs tabular-nums space-y-1">
                <div className="text-muted-foreground">
                  {[recorded.price != null ? `${t('deal_close_final_price')}: ${Number(recorded.price).toLocaleString()} ${recorded.currency || ''}`.trim() : null,
                    recorded.commission != null ? `${t('deal_close_final_commission')}: ${Number(recorded.commission).toLocaleString()}` : null,
                  ].filter(Boolean).join(' · ')}
                </div>
                {/* The door is ADMIN ONLY (owner, 2026-09-06: "don't show the edit option to
                    others"); the server refuses everyone else regardless. */}
                {role !== 'admin' ? null : !editVals ? (
                  <button type="button" className="text-primary hover:underline"
                    onClick={() => setEditVals({
                      price: recorded.price ?? '', commission: recorded.commission ?? '',
                      currency: recorded.currency || 'USD', reason: '',
                    })}>
                    <Icon name="pencil" size={11} /> {t('deal_figures_edit')}
                  </button>
                ) : (
                  <div className="space-y-1.5 pt-1">
                    <div className="grid grid-cols-2 gap-2 items-end">
                      <div>
                        <label className="block mb-1 text-xs text-muted-foreground">{t('deal_close_final_price')}</label>
                        <div className="flex gap-1.5">
                          <input className={input} inputMode="decimal" value={editVals.price}
                            onChange={e => setEditVals(v => ({ ...v, price: numeric(e.target.value) }))} />
                          <select className="rounded-lg border border-input px-2 text-sm bg-background"
                            value={editVals.currency} onChange={e => setEditVals(v => ({ ...v, currency: e.target.value }))}>
                            {['USD', 'GEL', 'EUR'].map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block mb-1 text-xs text-muted-foreground">{t('deal_close_final_commission')}</label>
                        <input className={input} inputMode="decimal" value={editVals.commission}
                          onChange={e => setEditVals(v => ({ ...v, commission: numeric(e.target.value) }))} />
                      </div>
                    </div>
                    {/* WHY, optional but asked for: a corrected fee moves money, and the next
                        reader of the event log is the person who has to explain it. */}
                    <input className={input} placeholder={t('deal_figures_edit_reason')} value={editVals.reason}
                      onChange={e => setEditVals(v => ({ ...v, reason: e.target.value }))} />
                    {editErr && <p className="text-xs text-destructive">{editErr}</p>}
                    <div className="flex justify-end gap-2">
                      <button className={btn('ghost')} onClick={() => { setEditVals(null); setEditErr('') }}>{t('cancel')}</button>
                      <button className={btn('primary')} disabled={editBusy} onClick={saveFigures}>
                        {editBusy ? t('desk_property_saving') : t('save')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!deal?.id && (closedAlready?.terms?.final_price != null || closedAlready?.terms?.final_commission_amount != null) && (
              <p className="text-xs text-muted-foreground tabular-nums">
                {[closedAlready.terms?.final_price != null
                    ? `${t('deal_close_final_price')}: ${Number(closedAlready.terms.final_price).toLocaleString()} ${closedAlready.terms?.final_currency || ''}`.trim()
                    : null,
                  closedAlready.terms?.final_commission_amount != null
                    ? `${t('deal_close_final_commission')}: ${Number(closedAlready.terms.final_commission_amount).toLocaleString()}`
                    : null].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        ) : !propertyId ? (
          // A meeting booked with no property (64% of them) can reach this drawer, and the
          // candidates fetch never runs without one — so it sat on "loading…" forever with
          // no error and no way out (fixed 2026-08-15). A closure is recorded against an AD,
          // so the honest answer is what to do first, not a spinner.
          <p className="text-sm rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-400">
            <Icon name="alert-triangle" /> {t('deal_close_no_property')}
          </p>
        ) : candidateErr ? (
          <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 flex items-center justify-between gap-3">
            <p className="text-sm text-destructive">{t('desk_load_failed')}</p>
            <button type="button" className={btn('ghost')} onClick={() => setCandidateVer(v => v + 1)}>
              {t('app_error_retry')}
            </button>
          </div>
        ) : candidates === undefined ? (
          <p className="text-sm text-muted-foreground">{t('desk_loading')}</p>
        ) : !listingId && !candidates?.length ? (
          // No ad at all on this property — nothing to record a lifecycle against.
          <p className="text-sm rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-400">
            <Icon name="alert-triangle" /> {t('deal_close_no_listing')}
          </p>
        ) : !listingId ? (
          // THE PICK. One row per ad, with what picking it means (sold vs rented_out) spelled
          // out, because that is the consequence an agent cannot otherwise see.
          //
          // GROUPED since 2026-08-01 (owner: "if there are many listings can we just group
          // them so it's easy for the agent to find the correct one?"). The list is
          // property-scoped by design, so it is every portal × every agent who reposted ×
          // rent and sale × expired ones — 9 ads by 3 different agents on the property this
          // was measured on. Presentation only: nothing is hidden, nothing is preselected,
          // and the 2026-07-30 "chosen, never inferred" ruling is untouched. Order is
          // yours-first, then still-closable, then the rest — the ad an agent is looking
          // for is overwhelmingly their own live one.
          <div className="rounded border border-border bg-background/60 px-3 py-2 space-y-1.5">
            <p className="text-xs text-muted-foreground">{t('deal_close_pick_listing')}</p>
            <div className="space-y-1">
              {groupedCandidates.map(([heading, rows]) => (
                <div key={heading} className="space-y-1">
                  {/* A heading only when there is more than one group — a single-group list
                      does not need a label telling it what it already obviously is. */}
                  {groupedCandidates.length > 1 && (
                    <p className="text-[11px] tracking-[.02em] text-muted-foreground pt-1">{heading}</p>
                  )}
                  {rows.map(c => (
                // A candidate whose deal KIND is already closed is shown DISABLED, not hidden:
                // dropping it would make the list look like the whole truth when it isn't, and
                // the agent would wonder where their ad went. It says why instead.
                <label key={c.listing_id} title={c.class_closed ? t('deal_close_class_closed') : undefined}
                  className={`flex items-center gap-2 min-h-11 md:min-h-10 px-2 rounded-md border transition-colors ${
                    c.class_closed ? 'border-border/50 opacity-50 cursor-not-allowed'
                      : pickedListing === c.listing_id ? 'border-primary bg-primary/5 cursor-pointer'
                      : 'border-border hover:border-primary/40 cursor-pointer'}`}>
                  <input type="radio" name={`close-listing-${propertyId}`} value={c.listing_id}
                    checked={pickedListing === c.listing_id} disabled={busy || c.class_closed}
                    onChange={() => setPickedListing(c.listing_id)} className="accent-primary shrink-0" />
                  <span className="min-w-0 flex-1 text-[13.5px] leading-[1.4] py-1">
                    {/* tOr, not t-with-|| : t() returns the KEY when a string is missing
                        (never falsy), so the || fallback was dead and an unmapped intent
                        printed `desk_listing_intent_lease` at the agent. */}
                    <span className="block break-words">
                      {[c.source, c.intent ? tOr(`desk_listing_intent_${c.intent}`, c.intent) : null,
                        c.price != null ? `${Number(c.price).toLocaleString()} ${c.currency || ''}`.trim() : null,
                       ].filter(Boolean).join(' · ')}
                    {/* WHOSE ad — the question the flat list could not answer. Only for
                        someone else's: labelling your own would be noise on every row. */}
                    {!c.is_mine && c.author_name && (
                      <span className="text-muted-foreground"> · {c.author_name}</span>
                    )}
                    </span>
                    {/* Two manual observations can legitimately look identical after an edit.
                        Never collapse them: expose stable identity + lifecycle so the human can
                        tell which advert the closure will be recorded against. */}
                    <span className="block text-[11px] text-muted-foreground break-all">
                      {[c.status || null, c.last_seen_at ? new Date(c.last_seen_at).toLocaleDateString() : null,
                        c.listing_id ? `ID ${c.listing_id.slice(0, 8)}` : null,
                       ].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className={`shrink-0 text-xs ${c.status === 'active' ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                    {c.implies_outcome ? tOr(`deal_close_outcome_${c.implies_outcome}`, c.implies_outcome) : '—'}
                  </span>
                </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* NAME the inherited ad (2026-08-01). The door brought a listing, so the picker
                is skipped — but "skipped" must not mean "silent": the agent still sees which
                ad this will be recorded against, because that decides the lifecycle written
                and who is credited. Inheriting a fact is fine; hiding it is not. */}
            {inheritedAd && (
              <p className="text-xs rounded border border-border bg-background/60 px-3 py-2">
                <Icon name="house" />{' '}
                {[inheritedAd.source,
                  inheritedAd.intent ? tOr(`desk_listing_intent_${inheritedAd.intent}`, inheritedAd.intent) : null,
                  inheritedAd.price != null ? `${Number(inheritedAd.price).toLocaleString()} ${inheritedAd.currency || ''}`.trim() : null,
                 ].filter(Boolean).join(' · ')}
                {meetingId && <span className="text-muted-foreground"> — {t('deal_close_from_meeting')}</span>}
              </p>
            )}
            {/* THREE different truths, and they used to share one sentence. The seeded
                figure is the price the owner was ASKED (min_price on the cooperation), not
                a recorded final — so the hint names it as such rather than calling it
                "inherited", which reads as already-agreed. And with no participation of the
                viewer's own there is nothing inherited at all: the boxes are empty on
                purpose and the line says why, instead of implying a number is missing. */}
            <p className="text-xs text-muted-foreground">
              {coop === null ? t('deal_close_no_coop_hint')
                : mine ? t('deal_close_inherited_hint')
                : t('deal_close_no_own_terms')}
            </p>
          </>
        )}
        {/* A CLOSED DEAL SHOWS NO SECOND FORM (owner-reported 2026-09-29): the recorded line said 50
            while the empty-looking form below it offered 500 from the viewer's own terms, which read as
            "it was recorded correctly". Recorded figures + the admin correction above are the only door. */}
        {!isClosed && (<>
        {/* ⚠️ `items-end`, and it is not cosmetic (owner-caught 2026-08-10 in the meeting
            modal's close drawer): the two labels are different LENGTHS in Georgian —
            "საბოლოო ფასი" fits one line and "საბოლოო საკომისიო (თანხა)" wraps to two — so
            with the default `stretch` each cell laid its own label out and the two input
            boxes came to rest a line apart. Bottom-aligning the cells pins the inputs to one
            baseline whatever the label does, which is what keeps this readable in a language
            whose strings are not the ones it was laid out in. `mb-1` on both labels so the
            gap above each box is the same too. */}
        <div className="grid grid-cols-2 gap-3 items-end">
          <div>
            <label className="block mb-1 text-xs font-medium text-muted-foreground">{t('deal_close_final_price')}</label>
            <div className="flex gap-1.5">
              <input className={input} inputMode="decimal" value={price} disabled={!editable}
                onChange={e => setPrice(numeric(e.target.value))} />
              <select className="rounded-lg border border-input px-2 text-sm bg-background" value={currency}
                disabled={!editable} onChange={e => setCurrency(e.target.value)}>
                {['USD', 'GEL', 'EUR'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-xs font-medium text-muted-foreground">{t('deal_close_final_commission')}</label>
            <input className={input} inputMode="decimal" value={commission} disabled={!editable}
              onChange={e => setCommission(numeric(e.target.value))} />
          </div>
        </div>
        {/* The cooperation's terms as a worked sum — the box asks for an AMOUNT, and "50%" beside
            an empty box is how a 500 fee got recorded as 50. */}
        {coopTerms && !isClosed && (
          <p className="text-[11px] text-muted-foreground -mt-1">
            {tLog('deal_close_coop_terms_hint', { pct: coopTerms.pct, base: coopTerms.base.toLocaleString(),
              amount: coopTerms.amount.toLocaleString(), cur: coopTerms.currency })}
          </p>
        )}
        {/* CO-BROKE (0449). The field above is the WHOLE fee — what the owner pays, which is
            what the contract says and what the agent has in front of them. This line does the
            subtraction out loud, because the number that reaches income is the net and an
            agent who is not shown the arithmetic will read their pay slip as an error. */}
        {cobrokeSplit.length > 0 && ourNet != null && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 space-y-0.5">
            <div className="text-xs font-medium">{t('deal_close_cobroke_title')}</div>
            {splitAmounts(parseMoneyInput(commission), cobrokeSplit).map((r, i) => (
              <div key={r.party_id || `us-${i}`} className="flex justify-between text-xs">
                <span className="truncate">{r.side === 'us' ? t('meeting_split_us') : (r.name || t('meeting_rep_external_badge'))} · {r.percent}%</span>
                <span className="tabular-nums shrink-0">{Number(r.amount).toLocaleString('en-US')} {currency}</span>
              </div>
            ))}
            <div className="text-[11px] text-muted-foreground pt-0.5">
              {tLog('deal_close_cobroke_note', { net: `${ourNet.toLocaleString('en-US')} ${currency}` })}
            </div>
          </div>
        )}
        {prospectiveSplit && (
          <DealSplit snapshot={prospectiveSplit} viewerAgentId={getCoreViewerAgentId()}
            role={role} agentsById={agentsById} />
        )}
        {/* Before the close: this meeting has no client, so no deal record can be created for
            it. Not a block — the money must be recordable today — but said before the press. */}
        {meetingId && clientPartyId === null && !notice && (
          <p className="text-xs text-amber-600 dark:text-amber-400">{t('deal_close_no_client_notice')}</p>
        )}
        {notice && <p className="text-xs text-amber-600 dark:text-amber-400">{notice}</p>}
        {err && <p className="text-xs text-destructive">{err === 'figures_required' ? t('deal_close_figures_required') : err}</p>}
        {/* A disabled button must say what is missing (rule: a lock names the action the
            viewer HAS). Shown only once the panel can otherwise close, so it reads as the
            last step rather than as a complaint about a form nobody has filled yet. */}
        {canClose && !hasFigures && (
          <p className="text-xs text-amber-600 dark:text-amber-400">{t('deal_close_figures_required')}</p>
        )}
        {/* The outsider's cut, missing. Named rather than generic: "record the split" means
            nothing without saying whose money it is and where the control lives. */}
        {cobrokeMissing && (
          <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 space-y-0.5">
            <div className="text-xs font-medium text-amber-700 dark:text-amber-400">
              <Icon name="alert-triangle" /> {tLog('deal_close_cobroke_missing', {
                name: cobrokeExpected.name || t('meeting_rep_external_badge') })}
            </div>
            <div className="text-[11px] text-muted-foreground">{t('deal_close_cobroke_missing_how')}</div>
          </div>
        )}
        </>)}
        <div className="flex justify-end gap-2 pt-1">
          <button className={btn('ghost')} onClick={onClose}>{t('cancel')}</button>
          {!isClosed && <button className={btn('primary')} disabled={busy || coop === undefined || !canClose || !hasFigures || cobrokeMissing} onClick={confirm}>
            {busy ? t('desk_property_saving') : t('deal_close_confirm')}
          </button>}
        </div>
      </div>
  )
}
